import type { QueryParamsList, QueryParamsRetrieve } from '../query'
import type { ListResponse, Resource, ResourceCreate, ResourceId, ResourceRel, ResourceSort, /* ResourceFilter */ ResourcesConfig, ResourceUpdate, } from '../resource'
import { ApiResource } from '../resource'
import type { AxervePayment, AxervePaymentType } from './axerve_payments'
import type { EventStore } from './event_stores'
import type { PaymentMethod } from './payment_methods'


type AxerveGatewayType = 'axerve_gateways'
type AxerveGatewayRel = ResourceRel & { type: AxerveGatewayType }
type AxervePaymentRel = ResourceRel & { type: AxervePaymentType }


export type AxerveGatewaySort = Pick<AxerveGateway, 'id' | 'name' | 'disabled_at'> & ResourceSort
// export type AxerveGatewayFilter = Pick<AxerveGateway, 'id' | 'name' | 'disabled_at'> & ResourceFilter


interface AxerveGateway extends Resource {
	
	readonly type: AxerveGatewayType

	/** 
	 * The payment gateway's internal name.
	 * @example ```"US payment gateway"```
	 */
	name: string
	/** 
	 * Indicates if the payment source is forced on the editable order upon receiving a successful event from the gateway.
	 * @example ```true```
	 */
	force_payments?: boolean | null
	/** 
	 * The payment gateway's API credential keys last digits.
	 * @example ```{"api_key":"********BW989"}```
	 */
	credential_keys?: Record<string, any> | null
	/** 
	 * Time at which this resource was disabled.
	 * @example ```"2018-01-01T12:00:00.000Z"```
	 */
	disabled_at?: string | null
	/** 
	 * The merchant login code.
	 * @example ```"xxxx-yyyy-zzzz"```
	 */
	login: string
	/** 
	 * The gateway webhook URL, generated automatically.
	 * @example ```"https://core.commercelayer.co/webhook_callbacks/axerve_gateways/xxxxx"```
	 */
	webhook_endpoint_url?: string | null

	payment_methods?: PaymentMethod[] | null
	event_stores?: EventStore[] | null
	axerve_payments?: AxervePayment[] | null

}


interface AxerveGatewayCreate extends ResourceCreate {
	
	/** 
	 * The payment gateway's internal name.
	 * @example ```"US payment gateway"```
	 */
	name: string
	/** 
	 * Indicates if the payment source is forced on the editable order upon receiving a successful event from the gateway.
	 * @example ```true```
	 */
	force_payments?: boolean | null
	/** 
	 * Send this attribute if you want to mark this resource as disabled.
	 * @example ```true```
	 */
	_disable?: boolean | null
	/** 
	 * Send this attribute if you want to mark this resource as enabled.
	 * @example ```true```
	 */
	_enable?: boolean | null
	/** 
	 * The merchant login code.
	 * @example ```"xxxx-yyyy-zzzz"```
	 */
	login: string
	/** 
	 * The gateway API key.
	 * @example ```"xxxx-yyyy-zzzz"```
	 */
	api_key: string

	axerve_payments?: AxervePaymentRel[] | null

}


interface AxerveGatewayUpdate extends ResourceUpdate {
	
	/** 
	 * The payment gateway's internal name.
	 * @example ```"US payment gateway"```
	 */
	name?: string | null
	/** 
	 * Indicates if the payment source is forced on the editable order upon receiving a successful event from the gateway.
	 * @example ```true```
	 */
	force_payments?: boolean | null
	/** 
	 * Send this attribute if you want to mark this resource as disabled.
	 * @example ```true```
	 */
	_disable?: boolean | null
	/** 
	 * Send this attribute if you want to mark this resource as enabled.
	 * @example ```true```
	 */
	_enable?: boolean | null
	/** 
	 * The merchant login code.
	 * @example ```"xxxx-yyyy-zzzz"```
	 */
	login?: string | null
	/** 
	 * The gateway API key.
	 * @example ```"xxxx-yyyy-zzzz"```
	 */
	api_key?: string | null

	axerve_payments?: AxervePaymentRel[] | null

}


class AxerveGateways extends ApiResource<AxerveGateway> {

	static readonly TYPE: AxerveGatewayType = 'axerve_gateways' as const

	async create(resource: AxerveGatewayCreate, params?: QueryParamsRetrieve<AxerveGateway>, options?: ResourcesConfig): Promise<AxerveGateway> {
		return this.resources.create<AxerveGatewayCreate, AxerveGateway>({ ...resource, type: AxerveGateways.TYPE }, params, options)
	}

	async update(resource: AxerveGatewayUpdate, params?: QueryParamsRetrieve<AxerveGateway>, options?: ResourcesConfig): Promise<AxerveGateway> {
		return this.resources.update<AxerveGatewayUpdate, AxerveGateway>({ ...resource, type: AxerveGateways.TYPE }, params, options)
	}

	async delete(id: string | ResourceId, options?: ResourcesConfig): Promise<void> {
		await this.resources.delete((typeof id === 'string')? { id, type: AxerveGateways.TYPE } : id, options)
	}

	async payment_methods(axerveGatewayId: string | AxerveGateway, params?: QueryParamsList<PaymentMethod>, options?: ResourcesConfig): Promise<ListResponse<PaymentMethod>> {
		const _axerveGatewayId = (axerveGatewayId as AxerveGateway).id || axerveGatewayId as string
		return this.resources.fetch<PaymentMethod>({ type: 'payment_methods' }, `axerve_gateways/${_axerveGatewayId}/payment_methods`, params, options) as unknown as ListResponse<PaymentMethod>
	}

	async event_stores(axerveGatewayId: string | AxerveGateway, params?: QueryParamsList<EventStore>, options?: ResourcesConfig): Promise<ListResponse<EventStore>> {
		const _axerveGatewayId = (axerveGatewayId as AxerveGateway).id || axerveGatewayId as string
		return this.resources.fetch<EventStore>({ type: 'event_stores' }, `axerve_gateways/${_axerveGatewayId}/event_stores`, params, options) as unknown as ListResponse<EventStore>
	}

	async axerve_payments(axerveGatewayId: string | AxerveGateway, params?: QueryParamsList<AxervePayment>, options?: ResourcesConfig): Promise<ListResponse<AxervePayment>> {
		const _axerveGatewayId = (axerveGatewayId as AxerveGateway).id || axerveGatewayId as string
		return this.resources.fetch<AxervePayment>({ type: 'axerve_payments' }, `axerve_gateways/${_axerveGatewayId}/axerve_payments`, params, options) as unknown as ListResponse<AxervePayment>
	}

	async _disable(id: string | AxerveGateway, params?: QueryParamsRetrieve<AxerveGateway>, options?: ResourcesConfig): Promise<AxerveGateway> {
		return this.resources.update<AxerveGatewayUpdate, AxerveGateway>({ id: (typeof id === 'string')? id: id.id, type: AxerveGateways.TYPE, _disable: true }, params, options)
	}

	async _enable(id: string | AxerveGateway, params?: QueryParamsRetrieve<AxerveGateway>, options?: ResourcesConfig): Promise<AxerveGateway> {
		return this.resources.update<AxerveGatewayUpdate, AxerveGateway>({ id: (typeof id === 'string')? id: id.id, type: AxerveGateways.TYPE, _enable: true }, params, options)
	}


	isAxerveGateway(resource: any): resource is AxerveGateway {
		return resource.type && (resource.type === AxerveGateways.TYPE)
	}


	relationship(id: string | ResourceId | null): AxerveGatewayRel {
		return super.relationshipOneToOne<AxerveGatewayRel>(id)
	}

	relationshipToMany(...ids: string[]): AxerveGatewayRel[] {
		return super.relationshipOneToMany<AxerveGatewayRel>(...ids)
	}


	type(): AxerveGatewayType {
		return AxerveGateways.TYPE
	}

}


const instance = new AxerveGateways()
export default instance

export type { AxerveGateway, AxerveGatewayCreate, AxerveGateways, AxerveGatewayType, AxerveGatewayUpdate }
